<script setup lang="ts">
import {
  type Hashtag,
  initPostState,
  PostContentsType,
  type PostsPayload,
} from '@store'
import { ROUTES } from '@configs'

const route = useRoute()
const etcStore = useEtcStore()
const postStore = usePostStore()
const isLoading = ref(false)
const isError = ref(false)
const query = computed(() => route.query as unknown as Hashtag)
const pageHashtagRef = ref<HTMLElement>()

definePageMeta({
  layout: 'hashtag',
})
useSetSEO()
useScrollRestoration()

// 쿼리 변경 감지 후 조건 업데이트
watch(
  () => query.value,
  (cur) => {
    if (typeof cur === 'object' && cur.hashtagSn && cur.hashtagNm) {
      cur.hashtagSn = Number(cur.hashtagSn)
      console.log('tracking watch postsHashtag:', cur)
      postStore.updateHashtagControl(cur)
    }
  },
  { immediate: true }
)

// 해시태그 선택시 목록 업데이트
watch(
  () => postStore.hashtagControl.hashtag,
  (cur, prev) => {
    const curString = JSON.stringify(cur)
    const prevString = JSON.stringify(prev)

    if (curString !== prevString) {
      useNavigations({
        url: ROUTES.HASHTAG.path,
        query: cur,
        type: 'replace',
      })
      handleOnFetchPostsByHashtag(true)
    }
  }
)

// 해시태그 조건으로 포스트 목록 조회
onMounted(() => {
  const { isChanged, newPayload } = checkEqualPostsPayload()
  if (postStore.postsByHashtag.items.length && !isChanged) return
  handleOnFetchPostsByHashtag(true, newPayload)
})

onUnmounted(() => {
  postStore.updateHashtagControl(initPostState.hashtagControl.hashtag)
  // postStore.postsByHashtag = initPostState.postsByHashtag
})

// SSR: 해시태그 랭킹 리스트 조회
await Promise.all([
  useAsyncData('hashtagRankingPrimary', () =>
    etcStore.fetchHashtagRanking(
      { pageSize: 10, rankPeriod: 'week' },
      'primary'
    )
  ),
  useAsyncData('hashtagRankingSecondary', () =>
    etcStore.fetchHashtagRanking(
      { pageSize: 30, rankPeriod: 'week' },
      'secondary'
    )
  ),
  useAsyncData('hashtagRankingTertiary', () =>
    etcStore.fetchHashtagRanking(
      { pageSize: 100, rankPeriod: 'semiannual' },
      'tertiary'
    )
  ),
  useAsyncData('baseHashtags', () => postStore.fetchBaseHashtags()),
])

// 활성화된 해시태그 변경
const handleOnChangeActiveHashtag = (hashtag: Hashtag) => {
  useNavigations({
    url: ROUTES.HASHTAG.path,
    query: { ...hashtag },
    type: 'replace',
  })
}

// 포스트 목록 조회 쿼리 비교
const checkEqualPostsPayload = () => {
  const newPayload = {
    searchHashtagNm: route.query.hashtagNm ? String(route.query.hashtagNm) : '',
  }
  const isChanged =
    newPayload.searchHashtagNm !==
    postStore.postsByHashtag.payload.searchHashtagNm

  return { isChanged, newPayload }
}

// 포스트 목록 조회
const handleOnFetchPostsByHashtag = async (
  refresh = false,
  payload?: Partial<PostsPayload>
) => {
  try {
    isLoading.value = true
    isError.value = false
    await postStore.fetchPosts(
      {
        ...postStore.postsByHashtag.payload,
        searchHashtagNm: postStore.hashtagControl.hashtag.hashtagNm,
        ...payload,
      },
      refresh,
      'hashtag'
    )
  } catch {
    isError.value = true
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <div ref="pageHashtagRef" class="page-hashtag">
    <h4 class="page-title">#{{ $t('hashtag') }}</h4>

    <NovaHashtagExtender
      v-if="etcStore.hashtagRankingSecondary.length"
      :active-hashtag="postStore.hashtagControl.hashtag"
      :hashtags="etcStore.hashtagRankingSecondary"
      :min-hashtags="16"
      :max-hashtags="30"
      @on-change-active-hashtag="handleOnChangeActiveHashtag"
    />

    <NovaBoxEmptyMessage
      v-if="
        postStore.postsByHashtag.refresh &&
        !postStore.postsByHashtag.items.length &&
        !isError
      "
      :message="
        $t('statusMessages.postsByHashtagSimple.empty', {
          hashtag: postStore.hashtagControl.hashtag.hashtagNm,
        })
      "
      :fill="true"
    />

    <NovaBoxEmptyMessage
      v-if="!postStore.postsByHashtag.items.length && isError"
      :message="
        $t('statusMessages.postsByHashtagSimple.error', {
          hashtag: postStore.hashtagControl.hashtag.hashtagNm,
        })
      "
      :fill="true"
    />

    <NovaRecyclerGrid
      v-if="postStore.postsByHashtag.items.length"
      :items="postStore.postsByHashtag.items"
      :is-last="postStore.postsByHashtag.last"
      :is-loading="isLoading"
      :is-error="isError"
      :is-refresh="postStore.postsByHashtag.refresh"
      :key-name="'cmtyNttSn'"
      :page-mode="true"
      :gap="12"
      :error-message="
        $t('statusMessages.postsByHashtagSimple.error', {
          hashtag: postStore.hashtagControl.hashtag.hashtagNm,
        })
      "
      :responsive="{
        mobile: {
          columns: 1,
          rowGap: 12,
          columnGap: 12,
          padding: { right: 10, left: 10 },
        },
        tablet: { columns: 1, rowGap: 12, columnGap: 12 },
        desktop: { columns: 1, rowGap: 12, columnGap: 12 },
      }"
      :bottom-white-space="60"
      :buffer="600"
      @load-more="handleOnFetchPostsByHashtag"
    >
      <template #item="{ item }">
        <NovaBoxPost
          :source="item"
          :view-type="PostContentsType.LIST"
          :list-view-height="'auto'"
          :open-detail-url="
            useRoutePathIdChange(ROUTES.HASHTAG_DETAIL.path, {
              id: item.cmtyNttSn,
            })
          "
        />
      </template>
    </NovaRecyclerGrid>

    <NovaModalAllHashtags />
  </div>
</template>

<style lang="scss" scoped>
.page-hashtag {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  padding-top: 40px;

  .page-title {
    @include text-style($text-heading2-bold);
    color: $color-text-2;
  }

  @include mobile {
    padding-top: 25px;

    .page-title {
      padding: 0 20px;
      @include text-style($text-heading3-bold);
    }

    :deep(.hashtag-extender) {
      padding: 0 20px;
    }

    .posts-list {
      :deep(.scroller) {
        padding: 0 10px;
      }
    }
  }
}
</style>
